import {WasteTile, WasteTileProps} from "../waste/tile.tsx";
import {useCallback, useMemo} from "react";
import {mothersOfWasteTqo, WasteStampFlavor} from "../rpc/webrpc-client.gen.ts";
import {
    useIsWasteDivisible,
    useIsWasteFullyDivided,
    useLastFlavoredWasteStamp,
    useWasteStatus,
} from "../waste/waste.tsx";
import {ActionButtonProps} from "../components/ActionButton.tsx";
import {useTranslation} from "react-i18next";
import {useNavigate} from "@tanstack/react-router";
import {useSelf} from "../agent/useSelf.ts";
import {
    useMothership,
    useRetireWasteMutation,
    useStampWasteMutation,
    useStoreWastePermanentMutation
} from "../tq/tqx.tsx";
import {buildTileActionButtons, SuspendedTile} from "../components/Tile.tsx";
import {Keyed, keyify} from "../util/arutil.tsx";
import {InfallibleUseResult, pendingUseResult, successUseResult} from "../util/hookutil.tsx";
import {errorize} from "../bomb/errorize.tsx";
import {useInfallibleQuery} from "../util/useInfallibleQuery.tsx";
import {useSystemConfig} from "../rpc/sysconf.tsx";

const commonActionProps = {color: "success", variant: "solid"} as const;

export function useInspectorWasteActions(args?: { wasteId: string }): InfallibleUseResult<Keyed<ActionButtonProps>[]> {
    const wasteId = args?.wasteId;
    const selfTqrv = useSelf();
    const self = selfTqrv.data;
    const canSupervise = !!self?.supervisorFor;
    const canInspect = !!self?.inspectorFor || canSupervise;
    const mama = useMothership();
    const {mutateAsync: performStamp} = useStampWasteMutation();
    const performFlavoredStamp = useCallback((flavor: WasteStampFlavor) => {
        if (wasteId) {
            return performStamp({food: {wasteId, flavor}});
        } else {
            return Promise.reject(errorize("no args"));
        }
    }, [performStamp, wasteId]);
    const lastFlavoredStampTqrv = useLastFlavoredWasteStamp(args);
    const lastFlavoredStamp = lastFlavoredStampTqrv.data;
    const motherTqrv = useInfallibleQuery(mothersOfWasteTqo(mama, args));
    const mothers = motherTqrv.data;
    const isDivisibleTqrv = useIsWasteDivisible(args);
    const isProperlyDividedTqrv = useIsWasteFullyDivided(args);
    const {t} = useTranslation();
    const navigate = useNavigate();
//    const isPrelimInspected = lastFlavoredStamp?.flavor === WasteStampFlavor.FINAL_INSPECT;
    const isFullyInspected = lastFlavoredStamp?.flavor === WasteStampFlavor.enum.FINAL_INSPECT;
    const {mutateAsync: sendPermanentStore} = useStoreWastePermanentMutation();
    const venueId = self?.supervisorFor?.id ?? self?.inspectorFor?.id;
    const performPermanentStore = useCallback(() => {
        if (wasteId && venueId) {
            return sendPermanentStore({wasteId, venueId});
        }
    }, [sendPermanentStore, venueId, wasteId]);
    const {mutateAsync: sendRetireWaste} = useRetireWasteMutation();
    const performRetireWaste = useCallback(() => {
        if (wasteId) {
            return sendRetireWaste({wasteId});
        } else {
            return Promise.reject(errorize("no args"));
        }
    }, [sendRetireWaste, wasteId]);
    // const downloadHistoryProps = useWasteHistoryDownloadProps({wasteId});

    const rv = useMemo(() => {
        if (!wasteId || selfTqrv.isPending || lastFlavoredStampTqrv.isPending || motherTqrv.isPending
            || isDivisibleTqrv.isPending || isProperlyDividedTqrv.isPending) {
            return pendingUseResult;
        }
        const isDivisible = isDivisibleTqrv.data;
        const isProperlyDivided = isProperlyDividedTqrv.data;

        const unkeyed: ActionButtonProps[] = [];
        if (isFullyInspected) {
            unkeyed.push({
                color: "danger", variant: "solid",
                disabled: true,
                children: t("deactivate"),
                perform: () => performRetireWaste()
                    .then(() => navigate({
                        to: "/",
                        replace: true,
                    })),
            });
        } else {
            const splitAction = {
                color: "primary", variant: isProperlyDivided ? "soft" : "solid",
                children: t("inspection.startSplit"),
                perform: () => navigate({
                    to: "/waste-split/$id",
                    params: {id: wasteId},
                }),
            } as const;

            if (canInspect) {
                const finalInspectAction = {
                    ...commonActionProps,
                    perform: () => performFlavoredStamp(WasteStampFlavor.enum.FINAL_INSPECT)
                        .then(() => performPermanentStore()),
                    //.then(() => performRetireWaste())
                    children: t("inspection.doInspectFinal"),
                } as const;
                const prelimInspectAction = {
                    color: "warning", variant: "solid",
                    perform: () => performFlavoredStamp(WasteStampFlavor.enum.PRELIM_INSPECT),
                    children: t("inspection.doInspectPrelim"),
                } as const;

                switch (lastFlavoredStamp?.flavor) {
                    case WasteStampFlavor.enum.PRELIM_INSPECT:
                        if (canSupervise) {
                            //                     unkeyed.push(downloadHistoryProps);
                            unkeyed.push(finalInspectAction);
                        }
                        break;

                    // this is commented out so that wastes newly added by
                    // inspectors go through inspection
                    // case undefined:
                    //     break;

                    default:
                        if (isDivisible) {
                            unkeyed.push(splitAction);
                        }
                        // do we have to worry about inspectors seeing this early?
                        // no, that is handled by having delivery be a requirement
                        // for bundles to show up as inspectable
                        // 2025.03.24 doesn't make sense to have prelim for supervisors
                        if ((!mothers || mothers.length === 0) && !canSupervise) {
                            unkeyed.push(prelimInspectAction);
                        }
                        unkeyed.push(finalInspectAction);
                }
            }
        }

        const rv = successUseResult(keyify(unkeyed));
        return rv;
    }, [canInspect, canSupervise, isDivisibleTqrv.data, isDivisibleTqrv.isPending,
        isFullyInspected, isProperlyDividedTqrv.data, isProperlyDividedTqrv.isPending,
        lastFlavoredStamp?.flavor, lastFlavoredStampTqrv.isPending, motherTqrv.isPending,
        mothers, navigate, performFlavoredStamp, performPermanentStore, performRetireWaste,
        selfTqrv.isPending, t, wasteId]);

    return rv;
}

export function InspectorWasteTile(props: WasteTileProps) {
    const {
        wasteId,
        children,
        ...rest
    } = props;
    const actionsTqrv = useInspectorWasteActions({wasteId});
    const syscfgTqrv = useSystemConfig();
    const actions = syscfgTqrv.data?.quickInspectorActions
        ? buildTileActionButtons(actionsTqrv.data) : undefined;
    const stateTqrv = useWasteStatus({wasteId});
    if (stateTqrv.isPending) {
        return <SuspendedTile/>;
    }

    return <WasteTile wasteId={wasteId} key={wasteId} actions={actions} {...rest}>
        {children}
    </WasteTile>
}


